import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { assert } from '@/util/assert';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { usePlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel';
import { useUserStore } from '@/stores/userStore';
import { useDeveloperSettings } from '@/stores/developerSettings';
import { useHipSpineFeatureGate } from '@/stores/featureToggles/useHipSpineFeatureGate';
import type { PlannerMode } from '@/views/3d-planner/store/plannerState';

type ExpandedPanel = 'cup' | 'stem' | null;

/** The sidebar panel implied by the planner mode — 'default'/'disabled' expand nothing. */
function panelForMode(mode: PlannerMode): ExpandedPanel {
    return mode === 'default' || mode === 'disabled' ? null : mode;
}

export const usePlannerSidebarViewModel = defineStore('planner-sidebar', () => {
    const planner = usePlannerStore();
    const scene = usePlannerSceneViewModel();
    const userStore = useUserStore();
    const developerSettings = useDeveloperSettings();
    const { enabled: hipSpineGateEnabled } = useHipSpineFeatureGate();

    const showContent = ref(false);
    const devToolsSelected = ref(false);

    const expandedPanel = computed<ExpandedPanel>({
        get: () => panelForMode(scene.plannerMode),
        set: (value) => {
            devToolsSelected.value = false;
            showContent.value = value !== null;
            assert(scene.plannerMode !== 'disabled', 'planner-store is disabled');
            scene.setMode(value ?? 'default');
        },
    });

    // Overlay flag. Setter is a deliberate no-op: clicking the v-overlay must
    // NOT dismiss it — the only thing that clears the overlay is the underlying
    // operation finishing (planner.currentOperation flipping back to null).
    const disabled = computed({
        get: () => planner.currentOperation !== null,
        set: () => {},
    });

    const disableSidebar = computed(
        () => planner.currentOperation !== null || userStore.isSurgicalSupportEngineer,
    );

    const developerToolsEnabled = computed(() => developerSettings.showDeveloperTools);

    const hipSpineAnalysisVisible = computed(
        () => hipSpineGateEnabled.value && expandedPanel.value === 'cup',
    );

    function openPanel(key: 'cup' | 'stem'): void {
        expandedPanel.value = key;
    }

    function openDevTools(): void {
        devToolsSelected.value = true;
        showContent.value = true;
    }

    function closeSidebar(): void {
        expandedPanel.value = null;
    }

    // Pinia stores outlive component mounts. When the active case changes,
    // the planner store resets plannerMode to 'default' (so expandedPanel
    // becomes null automatically), but the sidebar's own UI-only state
    // (showContent, devToolsSelected) does not — reset it explicitly so
    // sidebar visibility from a previous case doesn't leak into a new one.
    watch(
        () => planner.case?.caseId,
        () => {
            showContent.value = false;
            devToolsSelected.value = false;
        },
    );

    return {
        showContent,
        devToolsSelected,
        expandedPanel,
        disabled,
        disableSidebar,
        developerToolsEnabled,
        hipSpineAnalysisVisible,
        openPanel,
        openDevTools,
        closeSidebar,
    };
});
